<template>
  <!--
    Client-side htmljs renderer. Renders the {bundle, data, page} live in a SANDBOXED iframe via
    srcdoc — no `allow-same-origin`, so untrusted template JS cannot touch the parent app, its DOM,
    cookies or tokens (this is the secure replacement for the legacy FTSHtmlViewer innerHTML dump).
    The same assembleHost + runtime the backend render service uses → on-screen preview equals the PDF.
  -->
  <!-- The wrapper is the measured box: with `fit` the frame lays out at the page width and is
       scaled down (transform) to the wrapper's width; with `auto-height` the wrapper grows with
       the content (reported by the bridge). Neither prop set = the frame exactly as before. -->
  <div ref="wrap" class="fts-htmljs-renderer-wrap" :style="wrapStyle">
    <iframe
      ref="frame"
      class="fts-htmljs-renderer"
      :srcdoc="renderedSrcdoc"
      sandbox="allow-scripts"
      referrerpolicy="no-referrer"
      :style="frameStyle"
    />
  </div>
</template>

<script>
import { assembleHost } from "../commons/htmljs/assembleHost";
import { pageWidthPx, scaleFor, injectBridge } from "../commons/htmljs/frameFit";
import PAGEDJS_JS from "../commons/htmljs/libs/pagedjs.lib.js";
import debounce from "lodash.debounce";

let bridgeSeq = 0;

// Delay before a live edit repaints the preview. Each repaint re-runs assembleHost, the template
// JS, and Paged.js pagination inside the iframe — heavy enough that firing on every keystroke
// stutters the editor. Debouncing coalesces a burst of typing into one render once input settles.
const RENDER_DEBOUNCE_MS = 350;

// "Testing Only" watermark, injected CALLER-SIDE (here for the client preview/print, and
// DocController.PostRender for the server PDF) so the shared assembleHost — and the render service
// that mirrors it — stay generic: they render whatever bundle they are handed, with no stage logic.
// Shown when the document's development stage is "test" (parity with the legacy PdfSharp path,
// DocumentProcessor.cs). The markup MUST match DocController's copy so the client preview and the
// server PDF look the same. position:fixed makes Chromium repeat it on every printed page.
const TESTING_WATERMARK_HTML =
  '<div id="inistate-watermark" style="position:fixed;top:0;left:0;width:100%;height:100%;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:2147483647"><div style="transform:rotate(-30deg);font:700 96px/1 Arial,Helvetica,sans-serif;color:rgba(150,150,150,.28);text-transform:uppercase;letter-spacing:.12em;white-space:nowrap">Testing Only</div></div>';

export default {
  name: "FTSHtmlJsRenderer",
  props: {
    /** { mode, html, css, js } */
    bundle: { type: Object, required: true },
    /** DocumentDataModel ({ context, entry, entries, module, links }) */
    data: { type: Object, default: () => ({}) },
    /** { size, orientation, margin, header, footer } */
    page: { type: Object, default: () => ({}) },
    /** CSS height for the preview frame. */
    height: { type: String, default: "1123px" },
    /**
     * When to run Paged.js pagination:
     *  - "auto" (default): only when page.header/footer exist — they need running elements. Keeps
     *    the everyday end-user view light for chrome-less documents.
     *  - "always": paginate even without header/footer, so the page BOX (size / orientation /
     *    margin) is visible and live on screen. The design modal uses this — without pagination,
     *    @page is print-only CSS and edits to the Page tab would repaint an identical-looking
     *    flowing document (i.e. appear to do nothing).
     */
    paginate: { type: String, default: "auto" },
    /**
     * Scale-to-fit (analytic proposal P1): lay the frame out at the page width (page.size /
     * orientation / width) and scale it down to the wrapper's width when that is narrower — so an
     * A4-authored report reads whole in a 480px rail instead of clipping. Never upscales.
     */
    fit: { type: Boolean, default: false },
    /**
     * Auto-height: the wrapper follows the rendered content height, reported by the web-only
     * bridge script (postMessage from the sandboxed frame). `minHeight` floors it while the frame
     * is still loading. Without it the frame keeps the fixed `height` prop.
     */
    autoHeight: { type: Boolean, default: false },
    minHeight: { type: Number, default: 240 },
  },
  data() {
    // srcdoc is a data field (not a computed) so we control WHEN it recomputes: eagerly on mount,
    // then debounced on subsequent prop changes. A computed would recompute synchronously on every
    // keystroke, re-paginating the iframe each time.
    return {
      renderedSrcdoc: "",
      // Per-instance token the bridge echoes; the parent also matches on event.source.
      bridgeToken: "r" + (++bridgeSeq) + "-" + Math.random().toString(36).slice(2, 10),
      // Measured wrapper box (fit needs the width; a percent height needs the height).
      wrapWidth: 0,
      wrapHeight: 0,
      // Content height reported by the frame (auto-height).
      contentHeight: 0,
    };
  },
  computed: {
    pageWidth() {
      return pageWidthPx(this.page);
    },
    scale() {
      return scaleFor(this.wrapWidth, this.pageWidth, this.fit);
    },
    /** The frame's own height in its (unscaled) coordinate space, as a CSS string. */
    frameHeightCss() {
      if (this.autoHeight) return Math.max(this.minHeight, this.contentHeight) + "px";
      const h = String(this.height || "");
      // A percent height under a scale must be resolved against the measured wrapper, else the
      // scaled frame only fills scale x the box.
      if (this.scale < 1 && /%$/.test(h) && this.wrapHeight > 0)
        return Math.round(this.wrapHeight / this.scale) + "px";
      return h;
    },
    frameStyle() {
      const style = { width: "100%", height: this.frameHeightCss, border: "none", background: "#fff" };
      if (this.fit) {
        style.width = this.pageWidth + "px";
        if (this.scale < 1) {
          style.transform = "scale(" + this.scale + ")";
          style.transformOrigin = "0 0";
        }
      }
      return style;
    },
    wrapStyle() {
      const style = { display: "block", width: "100%" };
      if (this.autoHeight) {
        style.height = Math.round(Math.max(this.minHeight, this.contentHeight) * this.scale) + "px";
      } else if (/%$/.test(String(this.height || ""))) {
        style.height = this.height;
      }
      if (this.fit) style.overflow = "hidden";
      return style;
    },
  },
  watch: {
    // Repaint on any input change, but debounced so a burst of typing renders once.
    bundle: { handler: "scheduleRender", deep: true },
    data: { handler: "scheduleRender", deep: true },
    page: { handler: "scheduleRender", deep: true },
  },
  created() {
    // Per-instance debounce (defining it in `methods` would share one timer across all renderers).
    this.debouncedRender = debounce(this.renderNow, RENDER_DEBOUNCE_MS);
    // First paint is immediate — debouncing only smooths later edits, never the initial preview.
    this.renderNow();
  },
  mounted() {
    window.addEventListener("message", this.onFrameMessage);
    this.measureWrap();
    if (window.ResizeObserver) {
      this._ro = new ResizeObserver(this.measureWrap);
      if (this.$refs.wrap) this._ro.observe(this.$refs.wrap);
    } else {
      window.addEventListener("resize", this.measureWrap);
    }
  },
  beforeDestroy() {
    if (this.debouncedRender) this.debouncedRender.cancel();
    window.removeEventListener("message", this.onFrameMessage);
    window.removeEventListener("resize", this.measureWrap);
    if (this._ro) { try { this._ro.disconnect(); } catch (e) { /* ignore */ } }
  },
  methods: {
    /** Queue a debounced repaint of the preview after the current burst of input settles. */
    scheduleRender() {
      this.debouncedRender();
    },
    /**
     * Public: repaint immediately, dropping any pending debounced repaint (host "Render" buttons).
     * Also the escape hatch when a change the watchers can't see (or a stuck iframe) needs a
     * guaranteed rebuild — srcdoc is reassigned even if the markup is unchanged.
     */
    forceRender() {
      if (this.debouncedRender) this.debouncedRender.cancel();
      // Clear first so an identical srcdoc still reloads the frame (assignment alone wouldn't).
      this.renderedSrcdoc = "";
      this.$nextTick(this.renderNow);
    },
    /** Measure the wrapper box (fit width / percent height). */
    measureWrap() {
      const el = this.$refs.wrap;
      if (!el) return;
      this.wrapWidth = el.clientWidth;
      this.wrapHeight = el.clientHeight;
    },
    /**
     * Messages from OUR preview frame only (event.source + token): `size` keeps auto-height
     * current; `ready` re-emits the runtime's verdict ({ ok, error }) so a host can react to a
     * bundle that threw at runtime (the Bench's Regenerate affordance) instead of showing a blank.
     */
    onFrameMessage(e) {
      const frame = this.$refs.frame;
      if (!frame || e.source !== frame.contentWindow) return;
      const d = e.data;
      if (!d || d.source !== "inistate-htmljs" || d.token !== this.bridgeToken) return;
      if (typeof d.height === "number" && d.height > 0) this.contentHeight = Math.ceil(d.height);
      if (d.type === "ready") this.$emit("ready", { ok: d.ok !== false, error: d.error || null });
    },
    /** Rebuild the iframe srcdoc now, surfacing any build error inline instead of throwing. */
    renderNow() {
      try {
        this.contentHeight = 0;
        // The preview carries the bridge; print frames (buildHtml()) never do.
        this.renderedSrcdoc = injectBridge(this.buildHtml(), this.bridgeToken);
      } catch (e) {
        this.renderedSrcdoc = "<pre style='color:#b00;padding:16px;font:12px monospace'>" +
          String((e && e.message) || e) + "</pre>";
      }
    },
    /** assembleHost output + the web rendering of page.header/page.footer running chrome. */
    buildHtml() {
      return this.injectRunningChrome(assembleHost({ bundle: this.watermarkedBundle(), data: this.data, page: this.page }));
    },
    /**
     * Caller-side watermark: when the document's stage is "test", append the "Testing Only" overlay
     * to the bundle markup before handing it to the generic assembleHost — keeping assembleHost (and
     * the render service that mirrors it) free of business rules. Appending to bundle.html works for
     * both modes: in handlebars mode the string has no {{ }} so it compiles to itself; in js mode it
     * is plain root markup. Returns the bundle untouched when the stage is not "test".
     */
    watermarkedBundle() {
      const stage = this.data && this.data.template && this.data.template.development;
      if (stage !== "test") return this.bundle;
      return Object.assign({}, this.bundle, { html: (this.bundle.html || "") + TESTING_WATERMARK_HTML });
    },
    /**
     * Render page.header / page.footer in the browser. The render service emits these via Chromium's
     * native footerTemplate (a page.pdf arg, NOT assembleHost), so the web side reproduces them with
     * Paged.js to keep "preview == PDF" AND to fill page numbers in-browser. assembleHost stays
     * byte-identical to the service; everything here is a web-only layer applied to its output.
     *
     * Paged.js paginates the content into page boxes, places header/footer as CSS running elements
     * (@top-center / @bottom-center), then a handler fills .pageNumber/.totalPages per page — the
     * same authoring convention Chromium uses server-side, so one footer template works in both.
     * NOTE: Paged.js is a different pagination engine than Puppeteer, so page breaks can differ
     * slightly from the exported PDF; the server PDF (/doc/render) remains the byte-exact artifact.
     *
     * Repeating table headers: Chromium's print engine repeats <thead> on every page natively, but
     * Paged.js 0.4.x does not — a table split across pages loses its header from page 2 on. The
     * RTH handler in pagedBoot restores parity: after each page is laid out, any table continuation
     * fragment (data-split-from) gets a clone of its source table's <thead> prepended. Templates
     * must put header rows in a real <thead> (as htmljsStarters does) for BOTH engines to repeat.
     */
    injectRunningChrome(html) {
      const page = this.page || {};
      const header = page.header;
      const footer = page.footer;
      // Pagination runs when running chrome needs placing, or when the host asked for the page box
      // to always be visible (paginate="always" — see the prop). Without it, @page is print-only
      // and the on-screen preview is plain flowing HTML.
      if (!header && !footer && this.paginate !== "always") return html;

      const size = page.width && page.height ? page.width + " " + page.height : page.size || "A4";
      const orientation = page.orientation === "landscape" ? " landscape" : "";
      const m = page.margin || "16mm";

      const marginBoxes = [
        header ? "  @top-center{content:element(inistateHeader)}" : "",
        footer ? "  @bottom-center{content:element(inistateFooter)}" : "",
      ].filter(Boolean).join("\n");

      const css = [
        "<style>",
        "@page{size:" + size + orientation + ";margin:" + m + ";",
        marginBoxes,
        "}",
        header ? ".inistate-rc-header{position:running(inistateHeader)}" : "",
        footer ? ".inistate-rc-footer{position:running(inistateFooter)}" : "",
        ".inistate-rc{font-size:10px;color:#555}",
        // Page numbering via native CSS counters (reliable per-page; resolves inside the running-element
        // clone Paged.js renders on each page). Mirrors Chromium's .pageNumber/.totalPages convention,
        // so one footer template works in both the web preview and the server PDF.
        ".inistate-rc .pageNumber::before{content:counter(page)}",
        ".inistate-rc .totalPages::before{content:counter(pages)}",
        // NOTE: the on-screen page-box chrome (backdrop/gap/shadow) is deliberately NOT here — see
        // screenChrome below. Paged.js's Polisher reprocesses every stylesheet present when preview()
        // runs and DROPS @media screen blocks wholesale, so screen-scoped chrome placed here never
        // survives (it would vanish from the preview too, not just print).
        "</style>",
      ].filter(Boolean).join("\n");

      // Page-box chrome for the on-screen PREVIEW only (grey backdrop + inter-page gap + drop shadow)
      // so multi-page output reads as separate sheets. Injected AFTER Paged.js lays out (see pagedBoot):
      // a <style> appended post-preview escapes the Polisher, so its @media screen is honored — the
      // separation shows in the preview but never in the print dialog / exported PDF.
      const screenChrome =
        "@media screen{" +
        "html{background:#e9ebee}" +
        ".pagedjs_pages{padding:12px 0}" +
        ".pagedjs_page{background:#fff;margin:0 auto 12px;box-shadow:0 0 6px rgba(0,0,0,.22)}" +
        "}";

      const headerNode = header ? '<div class="inistate-rc inistate-rc-header">' + header + "</div>" : "";
      const footerNode = footer ? '<div class="inistate-rc inistate-rc-footer">' + footer + "</div>" : "";

      const pagedBoot =
        "<scr" + "ipt>(function(){" +
        // __inistatePagedReady resolves once pages are laid out — print() awaits it before printing.
        "var done;window.__inistatePagedReady=new Promise(function(r){done=r;});" +
        // Append the preview-only page-box chrome AFTER Paged.js finishes — appending here escapes its
        // Polisher (which strips @media screen blocks from stylesheets it reprocesses). Honored on
        // screen, ignored by print, so the page separation shows in the preview but not the PDF.
        "function inj(){try{var s=document.createElement('style');s.textContent=" +
        JSON.stringify(screenChrome) +
        ";(document.head||document.documentElement).appendChild(s);}catch(e){}}" +
        // Canvas preservation: Paged.js wrapContent() serialises <body> to a string (template.innerHTML
        // = body.innerHTML) and re-parses it, so every <canvas> Chart.js / template JS already painted
        // comes back as an EMPTY bitmap — the chart cards kept their CSS height but showed nothing.
        // Snapshot each painted canvas to a data URL before preview(), key it with a data attribute
        // (attributes survive the serialisation), then redraw onto the re-parsed copies afterwards.
        // toDataURL is fine in the opaque-origin frame: the canvases are origin-clean (no cross-origin
        // images are drawn into them).
        "var snaps=[];function snap(){document.querySelectorAll('canvas').forEach(function(c,i){try{" +
        "if(!c.width||!c.height)return;var k=String(i);c.setAttribute('data-inistate-canvas',k);" +
        "snaps.push({k:k,w:c.width,h:c.height,url:c.toDataURL()});}catch(e){}});}" +
        "function restore(){return Promise.all(snaps.map(function(s){return new Promise(function(res){" +
        "var t=document.querySelectorAll('canvas[data-inistate-canvas=\"'+s.k+'\"]');if(!t.length)return res();" +
        "var img=new Image();img.onload=function(){t.forEach(function(c){try{c.width=s.w;c.height=s.h;" +
        "c.getContext('2d').drawImage(img,0,0);}catch(e){}});res();};img.onerror=function(){res();};img.src=s.url;});}));}" +
        "function run(){var P=window.PagedModule;if(!P){done();return;}try{snap();" +
        // Repeating <thead> parity with Chromium print: when Paged.js splits a table across pages,
        // the continuation fragment carries data-split-from (= its data-ref); clone the source
        // table's <thead> onto it so the header repeats on every page like the exported PDF.
        "class RTH extends P.Handler{afterPageLayout(pg,_p,_bt,ck){" +
        "pg.querySelectorAll('table[data-split-from]').forEach(function(t){" +
        "if(t.querySelector(':scope>thead'))return;" +
        "var ref=t.getAttribute('data-split-from')||t.getAttribute('data-ref');" +
        "var src=ref&&ck&&ck.source&&ck.source.querySelector('[data-ref=\"'+ref+'\"]');" +
        "var h=src&&src.querySelector(':scope>thead');" +
        "if(h)t.insertBefore(h.cloneNode(true),t.firstChild);});}}" +
        "P.registerHandlers(RTH);" +
        "new P.Previewer().preview().then(function(){return restore();},function(e){if(window.console){console.error('[pagedjs]',e);}}).then(function(){inj();done();},function(){inj();done();});" +
        "}catch(e){if(window.console){console.error('[pagedjs]',e);}done();}}" +
        // Wait for the same ready() barrier the render service awaits (template + fonts settled).
        "(window.__inistateReady||Promise.resolve()).then(run,run);" +
        "})();</scr" + "ipt>";

      const pagedLib = "<scr" + "ipt>" + PAGEDJS_JS + "</scr" + "ipt>";

      // IMPORTANT: use FUNCTION replacements. The Paged.js source contains `$\`` and `$'` sequences;
      // as a plain string replacement those are special patterns ($' = text after the match), which
      // would splice duplicate copies of the document in and corrupt the scripts.
      const headInsert = pagedLib + css + "</head>";
      let out = html.indexOf("</head>") > -1 ? html.replace("</head>", () => headInsert) : pagedLib + css + html;
      // BOTH running elements go at the TOP of <body>. A Paged.js running element (position:running)
      // only becomes active on the page it is laid out on and the pages after it — so a footer placed
      // at the END of the body would appear ONLY on the last page. Emitting header AND footer before
      // the content makes them active from page 1. position:running pulls them out of flow, so their
      // source position doesn't shift the content.
      const lead = headerNode + footerNode;
      if (lead) out = out.replace("<body>", () => "<body>" + lead);
      const tail = pagedBoot + "</body>";
      out = out.indexOf("</body>") > -1 ? out.replace("</body>", () => tail) : out + pagedBoot;
      return out;
    },
    /**
     * Open the browser print dialog (Print / Save as PDF) for the current preview.
     * Prints the SAME assembleHost output the user sees, in a throwaway offscreen iframe.
     *
     * The print frame keeps allow-scripts (to render the bundle) + allow-modals (window.print),
     * and ADDS allow-same-origin. The latter is required: Chromium cannot rasterize an
     * opaque-origin (sandboxed, cross-origin) frame into a print snapshot, so without
     * allow-same-origin the dialog opens but every page comes out blank. This relaxation is
     * scoped to the throwaway print frame only — the on-screen preview iframe above stays
     * strictly allow-scripts (no same-origin), so untrusted template JS still can't reach the
     * parent during normal viewing. The print frame runs the same template JS the server render
     * already executes, and is discarded right after the dialog.
     *
     * @param {string} [fileName] Suggested name for the browser's "Save as PDF" (set as <title>).
     */
    print(fileName) {
      let html;
      try {
        html = this.buildHtml();
      } catch (e) {
        return;
      }
      this._printHtml(html, fileName);
    },
    /**
     * Bulk print (§5d, Flavor A): print a SINGLE-ENTRY template for many selected entries as one
     * combined document, fully client-side (no /doc/render, no PdfSharp). `dataModels` is an array of
     * per-entry DocumentDataModels (one `/doc/data` call each), already capped (≤20) and ordered by
     * the caller. Produces one print job → the browser's "Save as PDF" yields one combined PDF.
     *
     * VERIFY IN THE RUNNING APP (cannot be checked headless): (1) the auto-wrap assumes the template
     * is HANDLEBARS mode and binds via `entry.*` (block param `entry` shadows per row) — js-mode bulk
     * is a follow-up; (2) per-entry page-number RESET relies on `counter-reset:page` per section
     * surviving Paged.js — confirm "Page x" restarts each invoice; (3) per-entry TOTAL ("of y") still
     * reads the document-wide `counter(pages)` — a per-section total needs the grouped afterRendered
     * fill and is the known follow-up.
     */
    printBatch(dataModels, fileName) {
      let html;
      try {
        html = this.buildBatchHtml(dataModels);
      } catch (e) {
        return;
      }
      this._printHtml(html, fileName);
    },
    /**
     * Synthesize the combined batch document from N single-entry data models. Reuses the EXISTING
     * single-entry render path (assembleHost + injectRunningChrome) by wrapping the template body in
     * `{{#each entries as |entry|}}…{{/each}}` and feeding a data model whose `entries` = the selected
     * rows. Each entry renders in its own page-breaking section so invoices never share a sheet.
     */
    buildBatchHtml(dataModels) {
      const list = (Array.isArray(dataModels) ? dataModels : []).filter(Boolean);
      if (list.length <= 1) {
        // Nothing to batch — fall back to the normal single render (uses this.data).
        return this.buildHtml();
      }
      const bundle = this.watermarkedBundle();
      if (bundle && bundle.mode === "js") {
        // Imperative bundles run once against `inistate.entry`; they can't be auto-wrapped per row.
        // js-mode bulk is a follow-up (author a collection/Flavor-B template instead, §5e).
        throw new Error("Bulk print supports Handlebars templates only in this version.");
      }
      // Block param `entry` shadows the single-entry `entry` binding, so a template written against
      // `entry.*` renders each row unchanged. Each row is its own page-breaking, counter-resetting box.
      const wrappedHtml =
        '{{#each entries as |entry|}}<section class="inistate-batch-entry">' +
        ((bundle && bundle.html) || "") +
        "</section>{{/each}}";
      const batchBundle = Object.assign({}, bundle, { mode: "handlebars", html: wrappedHtml });
      // Combined model: keep the first entry's context/template/module/links; replace the payload with
      // the selected rows under `entries` (entry = null so a stray single-entry `{{entry}}` is empty).
      const base = list[0] || {};
      const batchData = Object.assign({}, base, {
        entry: null,
        entries: list.map(function (m) { return m && m.entry; }),
      });
      // Per-entry page break + page-counter reset (Page x restarts per invoice). Last entry doesn't
      // force a trailing blank page.
      const batchCss =
        "<style>.inistate-batch-entry{break-after:page;page-break-after:always;counter-reset:page}" +
        ".inistate-batch-entry:last-child{break-after:auto;page-break-after:auto}</style>";
      let html = this.injectRunningChrome(
        assembleHost({ bundle: batchBundle, data: batchData, page: this.page })
      );
      html =
        html.indexOf("</head>") > -1
          ? html.replace("</head>", function () { return batchCss + "</head>"; })
          : batchCss + html;
      return html;
    },
    /** Open the browser print dialog for a fully-built HTML document (shared by print + printBatch). */
    _printHtml(html, fileName) {
      // Give the browser's "Save as PDF" a sensible default filename: Chromium seeds the save name
      // from document.title, and assembleHost emits no <title>, so without this the suggested name is
      // "about:blank"/"document". This is what makes Print a viable no-backend "download".
      const name = (fileName == null ? "" : String(fileName)).trim();
      if (name) {
        const safe = name.replace(/[<>&]/g, function (c) {
          return c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&amp;";
        });
        html =
          html.indexOf("<head>") > -1
            ? html.replace("<head>", function () { return "<head><title>" + safe + "</title>"; })
            : "<title>" + safe + "</title>" + html;
      }
      // Trigger the dialog from inside the sandbox once the document has fully rendered: wait for
      // Paged.js pagination (__inistatePagedReady) when active, else the ready() barrier.
      // Fire the dialog at most once. focus() is isolated in its own try so a sandbox SecurityError
      // can't prevent print(). A safety timeout fires print() even if pagination/ready never settles
      // (the offscreen iframe gets no user gesture and Paged.js can stall on layout measurement).
      const trigger =
        "<scr" + "ipt>(function(){var fired=false;" +
        "function p(){if(fired)return;fired=true;" +
        "try{window.focus();}catch(e){}" +
        "try{window.print();}catch(e){if(window.console){console.error('[htmljs] print failed',e);}}}" +
        "var g=window.__inistatePagedReady||window.__inistateReady||Promise.resolve();" +
        "g.then(function(){setTimeout(p,80);},function(){setTimeout(p,80);});" +
        "setTimeout(p,4000);})();</scr" + "ipt>";
      const tail = trigger + "</body>";
      const doc = html.indexOf("</body>") > -1 ? html.replace("</body>", () => tail) : html + trigger;

      // Sized to A4 so width:100% / % layouts lay out at page width offscreen; the actual
      // paper size still comes from the @page rules in assembleHost.
      const landscape = (this.page && this.page.orientation) === "landscape";
      const f = document.createElement("iframe");
      f.setAttribute("sandbox", "allow-scripts allow-modals allow-same-origin");
      f.setAttribute("referrerpolicy", "no-referrer");
      f.style.cssText =
        "position:fixed;left:-10000px;top:0;border:0;background:#fff;" +
        "width:" + (landscape ? "1123px" : "794px") + ";height:" + (landscape ? "794px" : "1123px") + ";";
      f.srcdoc = doc;
      document.body.appendChild(f);
      // Reclaim the node well after the dialog closes (cross-origin afterprint isn't observable here).
      setTimeout(function () {
        try { document.body.removeChild(f); } catch (e) { /* already gone */ }
      }, 120000);
    },
  },
};
</script>

<style scoped>
.fts-htmljs-renderer-wrap {
  display: block;
  width: 100%;
}
.fts-htmljs-renderer {
  display: block;
  width: 100%;
}
</style>
